import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'sales.voucher.list',
  category: 'sales',
  title: 'Consulter les ventes de cartes cadeaux et packs',
  intent:
    "Consulter la liste des cartes cadeaux et des packs vendus, les filtrer par type et par statut, et gérer chaque vente : renvoyer l'e-mail cadeau, recharger, utiliser manuellement ou annuler.",
  context: {
    why: "Cartes cadeaux et packs, c'est du chiffre d'affaires encaissé d'avance : suivre ces ventes permet de gérer les soldes, de dépanner un client, et de garder une vue sur cet argent déjà rentré qui reste à consommer. Depuis cette liste, on retrouve une vente en filtrant par type ou par statut, on vérifie le solde restant d'un pack, on renvoie l'e-mail cadeau à un destinataire qui l'a égaré, on recharge un bon, on l'utilise manuellement au comptoir, ou on annule une vente en cas de problème. Cartes cadeaux et packs vivent dans la même liste : ce sont deux formes d'un même objet, distinguées par leur type.",
    use_cases: [
      'Retrouver les cartes cadeaux encore actives et leur solde.',
      "Vérifier ou recharger le solde d'un pack.",
      "Renvoyer l'e-mail cadeau à un destinataire qui ne le retrouve plus.",
      'Annuler une vente en cas de problème.',
    ],
  },
  required_scopes: ['sales.voucher.read'],
  ui_entry_points: [
    { route: '/sales/coupons', element: 'Écran « Cartes cadeaux » de Mes ventes' },
    { route: '/sales/packs', element: 'Écran « Packs vendus » de Mes ventes' },
    { route: '/sales/coupons', element: 'Bouton « Scanner un coupon » de l’en-tête' },
  ],
  api: {
    primary_operation_id: 'VoucherAppController_getVouchers',
    related_operation_ids: [
      'VoucherAppController_getVoucher',
      'VoucherAppController_getVoucherByCode',
      'VoucherAppController_previewUseVoucher',
      'VoucherAppController_useVoucher',
      'VoucherAppController_rechargeVoucher',
      'VoucherAppController_cancelVoucher',
      'VoucherAppController_resendGiftEmail',
    ],
  },
  steps: [
    '**Ouvrez « Mes ventes »**, écran « Cartes cadeaux » (ou « Packs vendus »).',
    '**Filtrez par type, par statut ou par prestation** pour réduire la liste.',
    '**Ouvrez une vente** pour voir son détail et son solde restant.',
    "**Agissez si besoin** : renvoyer l'e-mail cadeau, recharger le bon, l'utiliser manuellement, ou l'annuler.",
    "**Au comptoir, scannez le coupon** : bouton « Scanner un coupon », puis présentez le QR code de la carte cadeau devant la caméra (ou saisissez son code). La fenêtre d'utilisation s'ouvre directement avec le récapitulatif du coupon ; une fois utilisé, vous revenez à la liste.",
  ],
  gotchas: [
    "**Cartes cadeaux ET packs sont dans la même liste.** On les distingue par leur type (pack, prestation, montant) et par les filtres. Un pack vendu est une forme de bon, au même titre qu'une carte cadeau.",
    '**Le statut suit le cycle de vie de la vente.** En attente de paiement, puis actif, puis partiellement ou entièrement utilisé, et enfin expiré ou annulé. Le filtre par statut sert à retrouver rapidement, par exemple, tous les bons bientôt expirés.',
    "**L'utilisation peut venir du client ou de vous.** Un bon se consomme automatiquement quand le client réserve avec, ou manuellement quand vous l'encaissez au comptoir. Les deux mettent à jour le solde.",
    "**Renvoyer l'e-mail cadeau dépanne un destinataire.** Si la personne qui a reçu la carte ne retrouve plus son e-mail, ce bouton lui renvoie — inutile de recréer une vente.",
    "**Recharger ajoute au solde d'un bon existant.** Utile pour un pack qu'un client veut prolonger sans repartir de zéro.",
    "**Un coupon expiré, annulé ou pas encore payé peut quand même être utilisé au comptoir.** L'application vous prévient du motif avant de valider (« Utiliser quand même ») : c'est à vous de décider, par exemple pour un geste commercial. Le coupon garde son statut.",
    "**Le scanner utilise la caméra de l'appareil.** Dans le navigateur, il demande l'autorisation au premier scan et ne fonctionne que sur une connexion sécurisée (https). Dans l'application mobile, il faut la version 1.1.0 ou plus récente ; sur Android il passe par le scanner de Google (indisponible sur les téléphones sans services Google, comme les Huawei récents). Le champ « Code du coupon » reste toujours disponible : code imprimé, caméra refusée, ou douchette USB/Bluetooth réglée en clavier français.",
    "**Un coupon introuvable au scan** peut venir d'un autre de vos établissements (changez d'établissement), ou d'une fiche client supprimée : ses coupons ne sont plus accessibles au comptoir.",
  ],
  related_features: ['sales.voucher.manage', 'catalog.pack.manage', 'sales.voucher.redeem'],
  demo_script: {
    // Les lignes viennent du jeu de ventes de `backend:seed-e2e` (bons de type
    // AMOUNT, sans dépendance à un catalogue de prestations).
    duration_target_s: 30,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          "Cartes cadeaux et packs, c'est du chiffre d'affaires encaissé d'avance : voici comment suivre ces ventes.",
      },
      {
        action: 'click',
        target: sidebarNavTestId('/sales'),
        caption: "Menu latéral, « Mes ventes » : c'est ici que vous retrouvez toutes vos ventes.",
      },
      {
        action: 'click',
        target: sidebarSubTestId('/sales/coupons'),
        caption: 'Puis le sous-onglet « Cartes cadeaux », où cartes et packs cohabitent.',
        capture: true,
      },
      { action: 'click', target: TEST_IDS.list_row, caption: 'Ouvrez une vente pour en voir le détail.' },
      // `wait_for` avant la capture : en profil `fast` le runner enchaîne sans
      // battement, la capture partirait avant l'ouverture de la modale.
      {
        action: 'wait_for',
        target: TEST_IDS.voucher_detail_modal,
        caption: 'Client, statut et solde restant : le détail complet de la vente.',
        capture: true,
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Autonome, ancré code.) Liste des ventes de cartes cadeaux et de packs, avec filtres (type, statut)
// > et recherche par code ; actions : renvoyer l'e-mail, recharger, utiliser, annuler.
